﻿/* https://brandplatform.smartphoto.com/general/atoms/button/ */
import { expressEventListener, expressRemoveClass, expressAddClass } from "../../common/html";
import { IDisposable } from "../../utils/disposable";

export interface ICreateClickableOptions {
	readonly enable?: () => void;
	readonly disable?: () => void;
	readonly enableOnEnter?: boolean;
}

export interface IButton {
	readonly dispose: () => void;
	readonly enable: () => void;
	readonly disable: () => void;
	readonly updateText: (text: string) => void;
	readonly revertText: () => void;
}

export function createClickableElement(
	el: HTMLElement,
	onClick?: () => Promise<void>,
	options: ICreateClickableOptions = {},
) {
	let clickEvent: IDisposable;
	let enterEvent: IDisposable;
	const { enable, disable, enableOnEnter } = options;

	const onClicked = (e: Event) => {
		e.preventDefault();
		e.stopPropagation();
		if (onClick) {
			dispose();
			disable && disable();
			onClick().then(() => {
				enable && enable();
				addClickEvent();
			});
		}
	};

	const onEnter = (ev: KeyboardEvent) => {
		if (ev.key === "Enter" || ev.keyCode === 13) {
			ev.preventDefault();
			if (onClick) {
				dispose();
				disable && disable();
				onClick().then(() => {
					enable && enable();
					addClickEvent();
				});
			}
		}
	};

	const addClickEvent = () => {
		clickEvent = expressEventListener(el, "click", onClicked);
		if (enableOnEnter) enterEvent = expressEventListener(el, "keypress", onEnter);
	};

	const dispose = () => {
		clickEvent.dispose();
		enterEvent && enterEvent.dispose();
	};

	addClickEvent();

	return {
		addClickEvent,
		dispose,
	};
}

export function createButton(buttonEl: HTMLElement, onClick?: () => Promise<void>): IButton {
	const originalText = buttonEl.textContent;

	const enable = () => {
		// add component specific classes
		expressRemoveClass(buttonEl, "a-button--disabled");
		buttonEl.removeAttribute("disabled");
	};

	const disable = () => {
		expressAddClass(buttonEl, "a-button--disabled");
		buttonEl.setAttribute("disabled", "disabled");
	};

	const button = createClickableElement(buttonEl, onClick, {
		enable,
		disable,
	});

	const updateText = (text: string) => {
		if (text && buttonEl.innerText !== text) {
			buttonEl.innerText = text;
		}
	};

	const revertText = () => {
		updateText(originalText);
	};

	return {
		dispose: button.dispose,
		enable,
		disable,
		updateText,
		revertText,
	};
}
